import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'
type ColorTheme = 'default' | 'purple' | 'slate' | 'green' | 'neutral' | 'vercel' | 'blue'

const COLOR_THEME_CLASSES = ['theme-purple', 'theme-slate', 'theme-green', 'theme-neutral', 'theme-vercel', 'theme-blue'] as const

interface ThemeState {
  theme: Theme
  colorTheme: ColorTheme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  setColorTheme: (colorTheme: ColorTheme) => void
}

/**
 * Get the system theme preference
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

/**
 * Apply light/dark theme to document root
 */
function applyTheme(theme: Theme) {
  const root = document.documentElement
  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme

  root.classList.remove('light', 'dark')
  root.classList.add(effectiveTheme)
}

/**
 * Apply color theme to document root
 */
function applyColorTheme(colorTheme: ColorTheme) {
  const root = document.documentElement
  root.classList.remove(...COLOR_THEME_CLASSES)
  if (colorTheme !== 'default') {
    root.classList.add(`theme-${colorTheme}`)
  }
}

/**
 * Theme store with persistence
 * Manages dark/light theme preference and color theme
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      colorTheme: 'default',

      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },

      toggleTheme: () => {
        const currentTheme = get().theme
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark'
        applyTheme(newTheme)
        set({ theme: newTheme })
      },

      setColorTheme: (colorTheme) => {
        applyColorTheme(colorTheme)
        set({ colorTheme })
      },
    }),
    {
      name: 'pap-frontend-theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.theme)
          applyColorTheme(state.colorTheme)
        }
      },
    }
  )
)

// Initialize theme on module load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('pap-frontend-theme-storage')
  if (stored) {
    try {
      const { state } = JSON.parse(stored)
      if (state?.theme) {
        applyTheme(state.theme)
      }
      if (state?.colorTheme) {
        applyColorTheme(state.colorTheme)
      }
    } catch {
      // Ignore parse errors
    }
  }
}
